import { Quaternion, Vector3 } from 'three'
import type { ColorType } from '../core/enums'
import type { WaitingAreaSpec } from '../core/levelData'
import { CharacterAnim, hash01, hashRange } from './characterAnim'
import { Ease } from './tween'
import { WAITING_AREA_BOARDING_POINT, type GameplayTuning } from './tuning'

/**
 * Port of `WaitingArea` (TypeDefIndex 8607).
 *
 * Not a marker on the road — a bounded room that passengers walk into and mill
 * around in. This is the piece the whole puzzle turns on:
 *
 *  - It holds at most `maxOccupants`. Once full, no more can leave the queues.
 *  - A bus boards **only** from here, and only passengers whose colour matches:
 *    `HasSettledColor(c)` gates whether the bus is held at all, and
 *    `TakeSettledColor(c)` is the only way a passenger reaches a bus.
 *  - "Settled" means the passenger has finished walking in. Someone still in
 *    flight does not count, which is why `HasSettledColor` and `HasAnyColor`
 *    are separate methods in the original — the first drives boarding, the
 *    second drives the fail check.
 *
 * The crowd itself is a real kinematic simulation, ported from `LateUpdate`
 * (RVA 0x2133D18): people flow toward a per-colour band, push each other apart,
 * clamp inside the room, and lean into their own motion like roly-poly toys.
 * That used to be a comment here saying it was "presentation" and skipped — it
 * is not skipped any more, and it is not only presentation: the packing is what
 * decides who ends up where, and the colour banding is what makes a jam legible.
 *
 * Everything below works in the room's **local Unity space**. The view converts.
 */

/**
 * 🔍 Both exit-door tweens are `0x3e19999a` = 0.15 s, hard-coded at the call
 * site in `OpenExitDoors` / `CloseExitDoors` — not a `[SerializeField]`.
 */
const DOOR_LEG_SECONDS = 0.15

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

/** `WaitingArea.CrowdEntry` (TypeDefIndex 8605). */
export interface CrowdEntry {
  color: ColorType
  /** `CrowdEntry.Character`'s animation state — wobble and sway. */
  readonly anim: CharacterAnim
  /** Stable per-person value standing in for `Random`. */
  readonly seed: number

  /** `SimPosition` (0x18) — where the solver wants this person. */
  readonly sim: Vector3
  /** The drawn position, chasing `sim` through `Vector3.SmoothDamp`. */
  readonly visual: Vector3
  /** `VisualVelocity` (0x24) — SmoothDamp's own state. */
  readonly velocity: Vector3

  /** `IsJumping` (0x30) — still walking in through the door. */
  isJumping: boolean

  /** `TiltX` / `TiltZ` (0x34, 0x38), degrees. */
  tiltX: number
  tiltZ: number
  /** `TiltVelocityX` / `TiltVelocityZ` (0x3C, 0x40). */
  tiltVelocityX: number
  tiltVelocityZ: number
  /** `WasMoving` (0x48) / `StoppedTimer` (0x4C). */
  wasMoving: boolean
  stoppedTimer: number

  /**
   * The `DOLocalMove` walk-in: where from, where to, and how far through it.
   *
   * `walkTo` is held separately from `sim` on purpose. In the original the
   * tween owns its own end value and `LateUpdate` copies the transform *back*
   * into `SimPosition` every frame while `IsJumping` — so `SimPosition` is a
   * mirror during the walk-in, not the destination. Using `sim` as both makes
   * the target chase the walker and they stall short of the door.
   */
  readonly walkFrom: Vector3
  readonly walkTo: Vector3
  walkElapsed: number
}

/** `WaitingArea.CHARACTER_Y`, a private const. */
const CHARACTER_Y = 0.2

/**
 * ⚠️ **DELIBERATE DEVIATION from `WaitingArea.LateUpdate` step 3.** Everything
 * else in this file is a reading; this is a decision, and it is logged as one.
 *
 * The original gives every member of a colour the **same** attractor — one
 * point per colour band, `(Lerp(±0.4 * boundsX, index / (count - 1)), 0)` — and
 * lets the separation pass sort out the pile. Measured on the shipped
 * constants that never converges: at 94 occupants the room settles at a median
 * neighbour distance of 1.23 against its own 1.40 floor, worst pair 0.65, and
 * 46 pairs of drawn bodies (1.24 wide) interpenetrate by up to 0.60 — half a
 * torso. Isolated to the flow rather than to any one constant: with `flowSpeed`
 * at 0 the same room converges cleanly at every radius tried, and raising
 * `overlapIterations` to the top of its `[Range(1, 10)]` still leaves 12
 * overlapping pairs. So the pile-up is what the original's own arithmetic
 * produces, and the four constants that would change it all come out of the
 * `.ctor` field-initialiser block already proven stale for three other fields.
 *
 * The user looked at it and asked for something else: same-colour passengers
 * still grouped, but **evenly spread, at an even density, filling the whole
 * hall when the room is full**. That is a lattice, so this is a lattice — one
 * slot per person instead of one attractor per colour.
 *
 * Only the *target* changes. The flow that walks toward it is still the
 * original's, doubled X and dead zone and all, and so are the separation pass,
 * the bounds clamp, the `SmoothDamp` and the roly-poly spring.
 *
 * ❓ To retire this and go back to the reading: a frame of
 * `reference/gameplay.mp4` with the room full, which would say whether the
 * original really does pile up like its arithmetic implies. That needs ffmpeg,
 * which this machine does not have.
 */
export interface CrowdLattice {
  /** People per column, top to bottom of the room. */
  rows: number
  /** Columns across the room, ordered by `ColorType`. */
  cols: number
  /** Spacing between columns and between rows, constant at every occupancy. */
  colPitch: number
  rowPitch: number
}

/**
 * The lattice a **full** room uses: the one that has to fill the hall exactly.
 *
 * `rows` is picked so the block matches the room's own aspect ratio, which is
 * what makes the two pitches come out close to each other — an even density
 * rather than a wide-spaced grid in one axis and a squeeze in the other. The
 * span is the same rectangle the bounds clamp allows, inset one radius on all
 * four sides, so the outermost slots sit exactly on the clamp and nobody is
 * placed somewhere the clamp would then move them.
 *
 * On the shipped constants (20.2 x 8.8, radius 0.69, 94 occupants) this is
 * 6 rows x 16 columns, pitch 1.255 x 1.484 — and 1.255 clears the drawn body
 * width of 1.24, so a full room has no overlapping pair at all.
 */
export function fullCrowdLattice(tuning: GameplayTuning): CrowdLattice {
  const usableX = Math.max(0, tuning.waitingAreaBounds.x - 2 * tuning.waitingAreaCharacterRadius)
  const usableZ = Math.max(0, tuning.waitingAreaBounds.y - 2 * tuning.waitingAreaCharacterRadius)
  const capacity = Math.max(1, tuning.waitingAreaMaxOccupants)
  const aspect = usableZ > 1e-6 ? usableX / usableZ : 1

  const rows = Math.max(1, Math.round(Math.sqrt(capacity / Math.max(aspect, 1e-6))))
  const cols = Math.max(1, Math.ceil(capacity / rows))
  return {
    rows,
    cols,
    colPitch: cols > 1 ? usableX / (cols - 1) : 0,
    rowPitch: rows > 1 ? usableZ / (rows - 1) : 0,
  }
}

/**
 * The block `count` people occupy — the full lattice's **pitch**, on a smaller
 * frame.
 *
 * Holding the pitch fixed is what keeps the density even as the room drains:
 * a half-full room is a smaller block of the same spacing, not the same block
 * spread thinner. The shape is taken from the full lattice's aspect for the
 * same reason it was there — a run of 6 should read as a clump, not as a
 * one-wide column standing the whole depth of the hall.
 */
export function crowdLattice(count: number, tuning: GameplayTuning): CrowdLattice {
  const full = fullCrowdLattice(tuning)
  if (count <= 1) return { ...full, rows: 1, cols: 1 }

  const aspect = full.cols / full.rows
  let rows = Math.min(full.rows, Math.max(1, Math.ceil(Math.sqrt(count / aspect))))
  let cols = Math.ceil(count / rows)
  if (cols > full.cols) {
    cols = full.cols
    rows = Math.min(full.rows, Math.ceil(count / cols))
  }
  return { rows, cols, colPitch: full.colPitch, rowPitch: full.rowPitch }
}

/**
 * Where slot `index` of `count` stands, in room-local space.
 *
 * Column-major: a colour therefore owns a contiguous run of **columns**, which
 * is the "grouped by colour" half of the request, and the run order follows
 * `ColorType` because the caller sorts by it — the same left-to-right order the
 * original's bands had.
 *
 * The block is centred on `flowTargetXZ`, so the field still means what it
 * meant and the two tests that pin the flow to it still bite. The last column
 * is usually short; it is centred on its own occupants rather than hung from
 * the top, so the ragged edge is symmetric.
 */
export function crowdSlotTarget(
  index: number,
  count: number,
  tuning: GameplayTuning,
  lattice: CrowdLattice = crowdLattice(count, tuning),
  out: { x: number; z: number } = { x: 0, z: 0 },
): { x: number; z: number } {
  const rows = Math.max(1, lattice.rows)
  const col = Math.floor(index / rows)
  const row = index - col * rows
  // How many people actually stand in this column — the last one runs short.
  const inColumn = Math.max(1, Math.min(rows, count - col * rows))

  out.x = tuning.waitingAreaFlowTargetX + (col - (lattice.cols - 1) * 0.5) * lattice.colPitch
  out.z = tuning.waitingAreaFlowTargetZ + (row - (inColumn - 1) * 0.5) * lattice.rowPitch
  return out
}

/** `LateUpdate` opens with `Mathf.Min(Time.deltaTime, 0.033f)`. */
const MAX_STEP = 0.033

/** Squared visual speed above which a passenger counts as moving. */
const MOVING_SPEED_SQ = 0.35

/** `Mathf.Clamp(tiltVelocity, -400f, 400f)`. */
const MAX_TILT_VELOCITY = 400

export class WaitingAreaRuntime {
  readonly index: number
  readonly position: Vector3
  readonly rotation: Quaternion
  /** World position of `BoardingPoint`, projected onto the road for the stop. */
  readonly boardingPointWorld: Vector3

  readonly crowd: CrowdEntry[] = []

  /** Percent on the main spline where a bus is considered to be at this stop. */
  stopPercent = 0

  /** `AreaRuntime.HeldBus` — the bus currently held here, if any. */
  heldBusId = -1
  /** `AreaRuntime.BoardingCooldown` */
  boardingCooldown = 0
  /** `AreaRuntime.InFlightBoardings` — passengers mid-jump onto the bus. */
  inFlightBoardings = 0
  /** `AreaRuntime.DepartWhenSettled` — set when the held bus filled up. */
  departWhenSettled = false
  /** `WaitingArea._doorsOpen` (0x D8) — the guard both door methods open with. */
  exitDoorsOpen = false

  /**
   * Seconds since the current door `Sequence` was built, or -1 when no
   * sequence has ever run. Both leaves share it: `OpenExitDoors` builds one
   * sequence per leaf but from the same call, with the same durations, so they
   * are always in lockstep.
   */
  private doorElapsed = -1
  /** Where the leaves stood when that sequence was built. See `updateDoors`. */
  private doorFromX = 0
  private doorFromZ = 0

  /**
   * Offset of the **right** leaf from its authored closed pose. The left leaf
   * mirrors X and shares Z:
   *
   *     doorLeft :  x = closed.x - doorOffsetX,  z = closed.z + doorOffsetZ
   *     doorRight:  x = closed.x + doorOffsetX,  z = closed.z + doorOffsetZ
   *
   * Read by the view; nothing in the rules depends on it.
   */
  doorOffsetX = 0
  doorOffsetZ = 0

  /**
   * 🔍 `WaitingArea.OpenExitDoors` (RVA 0x21354D8):
   *
   *     if (_doorsOpen) return;
   *     _doorsOpen = true;
   *     if (doorLeft != null) {
   *         _doorLeftTween?.Kill();
   *         _doorLeftTween = DOTween.Sequence()
   *             .Append(doorLeft.DOLocalMoveZ(_doorLeftClosed.z + doorOpenSlideZ, 0.15f).SetEase(?))
   *             .Append(doorLeft.DOLocalMoveX(_doorLeftClosed.x - doorOpenSlideDistance, 0.15f).SetEase(?));
   *     }
   *     if (doorRight != null) { ... .z + doorOpenSlideZ ... .x + doorOpenSlideDistance ... }
   *
   * Note the order: the leaves **retreat in Z first**, then part sideways.
   * The early `return` matters — a second call while already open does not
   * restart the slide.
   */
  openExitDoors(): void {
    if (this.exitDoorsOpen) return
    this.exitDoorsOpen = true
    this.rebuildDoorSequence()
  }

  /**
   * 🔍 `WaitingArea.CloseExitDoors` (RVA 0x21357A8) — the same body with
   * `if (_doorsOpen != true) return;`, and the two `Append`s **swapped**:
   * X back to `_doorLeftClosed.x` first, then Z. So the leaves come together
   * before they step forward again.
   */
  closeExitDoors(): void {
    if (!this.exitDoorsOpen) return
    this.exitDoorsOpen = false
    this.rebuildDoorSequence()
  }

  /**
   * `_doorLeftTween?.Kill(); _doorLeftTween = DOTween.Sequence()`.
   *
   * `Kill()` (no `complete`) leaves the leaf exactly where it had got to, and
   * the fresh `DOLocalMove*` tweens read their start value off the transform —
   * so an interrupted slide carries on from wherever it was, over a full fresh
   * 0.15 s per leg. That is what `doorFromX` / `doorFromZ` capture.
   */
  private rebuildDoorSequence(): void {
    this.doorElapsed = 0
    this.doorFromX = this.doorOffsetX
    this.doorFromZ = this.doorOffsetZ
  }

  /**
   * Advance the door `Sequence`.
   *
   * Two `Append`ed tweens of 0.15 s each (`0x3e19999a`), so 0.3 s end to end,
   * and the second one holds its start value until the first has finished —
   * which is what `Append` means.
   *
   * ❓ Both tweens carry a `SetEase`, but IL2CPP erased the argument, so the
   * curve between the two keys is unknown. Linear here. (Unlike the elevator
   * lids, this one is a `Tweener` ease and therefore does apply — the usual
   * "`Sequence` ignores `SetEase`" escape hatch does not help.)
   */
  private updateDoors(dt: number): void {
    if (this.doorElapsed < 0) return
    this.doorElapsed += dt

    const targetX = this.exitDoorsOpen ? this.tuning.waitingAreaDoorOpenSlideDistance : 0
    const targetZ = this.exitDoorsOpen ? this.tuning.waitingAreaDoorOpenSlideZ : 0

    const first = clamp01(this.doorElapsed / DOOR_LEG_SECONDS)
    const second = clamp01((this.doorElapsed - DOOR_LEG_SECONDS) / DOOR_LEG_SECONDS)
    // Opening appends Z then X; closing appends X then Z.
    const zLeg = this.exitDoorsOpen ? first : second
    const xLeg = this.exitDoorsOpen ? second : first

    this.doorOffsetX = this.doorFromX + (targetX - this.doorFromX) * xLeg
    this.doorOffsetZ = this.doorFromZ + (targetZ - this.doorFromZ) * zLeg

    if (this.doorElapsed >= 2 * DOOR_LEG_SECONDS) this.doorElapsed = -1
  }

  constructor(
    index: number,
    spec: WaitingAreaSpec,
    private tuning: GameplayTuning,
  ) {
    this.index = index
    this.position = spec.position.clone()
    this.rotation = spec.rotation.clone()
    this.boardingPointWorld = WAITING_AREA_BOARDING_POINT.clone()
      .applyQuaternion(spec.rotation)
      .add(spec.position)
  }

  // -- capacity ------------------------------------------------------------

  /** `WaitingArea.HasFreeSpot` = `_crowd.Count < maxOccupants`. */
  get hasFreeSpot(): boolean {
    return this.crowd.length < this.tuning.waitingAreaMaxOccupants
  }

  /** `WaitingArea.FreeSpotCount` */
  get freeSpotCount(): number {
    return Math.max(0, this.tuning.waitingAreaMaxOccupants - this.crowd.length)
  }

  get isEmpty(): boolean {
    return this.crowd.length === 0
  }

  // -- colours -------------------------------------------------------------

  /** `WaitingArea.HasSettledColor` — drives whether a bus is held here. */
  hasSettledColor(color: ColorType): boolean {
    return this.crowd.some((m) => !m.isJumping && m.color === color)
  }

  /** `WaitingArea.HasAnyColor` — drives the fail check; ignores settling. */
  hasAnyColor(color: ColorType): boolean {
    return this.crowd.some((m) => m.color === color)
  }

  /** `WaitingArea.TakeSettledColor` — the only way a passenger boards. */
  takeSettledColor(color: ColorType): CrowdEntry | null {
    const at = this.crowd.findIndex((m) => !m.isJumping && m.color === color)
    if (at < 0) return null
    return this.crowd.splice(at, 1)[0]
  }

  // -- flow ----------------------------------------------------------------

  /** `WaitingArea._activeColors` — the colours present, sorted by `ColorType`. */
  private activeColors: ColorType[] = []

  /** Scratch for the slot ranking in `lateUpdateStep`; reused, never read out. */
  private readonly slotOrder: CrowdEntry[] = []

  /**
   * Test seam. `crowdSeparation.test.ts` checks the different-colour steering,
   * which resolves through this list — and the list is rebuilt from whoever is
   * actually in the room at the top of every step, so the test has to read the
   * order back rather than assume one.
   */
  get activeColorsForTest(): readonly ColorType[] {
    return this.activeColors
  }
  private received = 0

  /**
   * `WaitingArea.Receive` — a passenger walks in from the queue and takes a spot.
   *
   *     if (_crowd.Count >= maxOccupants) return false;
   *     var c = character.GetColor();
   *     if (!_activeColors.Contains(c)) { _activeColors.Add(c); _activeColors.Sort(); }
   *     float targetX = _activeColors.Count < 2
   *         ? flowTargetXZ.x
   *         : Mathf.Lerp(areaBoundsSize.x * -0.4f, areaBoundsSize.x * 0.4f,
   *                      Mathf.Clamp01(_activeColors.IndexOf(c) / (float)(_activeColors.Count - 1)));
   *     targetX += Random.Range(-0.8f, 0.8f);
   *     targetX  = Mathf.Clamp(targetX, doorWidth * -0.5f, doorWidth * 0.5f);
   *     float targetZ = Random.Range(-0.5f, 1.0f) + doorLocalZ + walkInDistance;
   *     var entry = new CrowdEntry { Character = character,
   *                                  SimPosition = new Vector3(targetX, 0.2f, targetZ),
   *                                  IsJumping = true };
   *     _crowd.Add(entry);
   *     character.transform.SetParent(transform, true);
   *     character.WalkAnimation(transform.TransformPoint(entry.SimPosition)
   *                             - character.transform.position);
   *     character.transform.DOKill();
   *     character.transform.DOLocalMove(entry.SimPosition, jumpDuration)
   *              .SetEase(...).OnComplete(...);
   *
   * The room sorts itself by colour: each colour gets a band across the width,
   * ordered by `ColorType`. That is what makes a jam readable on screen — one
   * band swelling while its bus is elsewhere.
   *
   * `fromWorld` is where the passenger stood in the queue, in Unity world
   * space; the walk-in starts there instead of teleporting to the door.
   * `Random.Range` is replaced by a deterministic hash so replays and the
   * headless solver stay reproducible.
   */
  receive(color: ColorType, fromWorld: Vector3 | null = null): boolean {
    if (!this.hasFreeSpot) return false

    if (!this.activeColors.includes(color)) {
      this.activeColors.push(color)
      this.activeColors.sort((a, b) => a - b)
    }

    const t = this.tuning
    const bounds = t.waitingAreaBounds
    const seed = this.received++ + this.index * 977

    let targetX: number
    if (this.activeColors.length < 2) {
      targetX = t.waitingAreaFlowTargetX
    } else {
      const k = Math.max(
        0,
        Math.min(1, this.activeColors.indexOf(color) / (this.activeColors.length - 1)),
      )
      const min = bounds.x * -0.4
      const max = bounds.x * 0.4
      targetX = min + (max - min) * k
    }
    targetX += hashRange(seed * 7 + 3, -0.8, 0.8)
    const halfDoor = t.waitingAreaDoorWidth * 0.5
    targetX = Math.max(-halfDoor, Math.min(halfDoor, targetX))

    const targetZ =
      hashRange(seed * 13 + 5, -0.5, 1.0) + t.waitingAreaDoorLocalZ + t.waitingAreaWalkInDistance

    const from = new Vector3(targetX, CHARACTER_Y, targetZ)
    if (fromWorld) this.toLocal(fromWorld, from)

    const anim = new CharacterAnim()
    anim.walkAnimation(targetX - from.x, 0, targetZ - from.z)

    this.crowd.push({
      color,
      anim,
      seed,
      sim: new Vector3(targetX, CHARACTER_Y, targetZ),
      visual: from.clone(),
      velocity: new Vector3(),
      isJumping: true,
      tiltX: 0,
      tiltZ: 0,
      tiltVelocityX: 0,
      tiltVelocityZ: 0,
      wasMoving: false,
      stoppedTimer: 0,
      walkFrom: from,
      walkTo: new Vector3(targetX, CHARACTER_Y, targetZ),
      walkElapsed: 0,
    })
    return true
  }

  /** Local Z of the exit door, where boarding passengers gather. */
  get exitDoorLocalZ(): number {
    return this.tuning.waitingAreaExitDoorLocalZ
  }

  /** `BoardOnePerson`: `Random.Range(ExitDoorWidth * -0.5f, ExitDoorWidth * 0.5f)`. */
  exitDoorLocalX(seed: number): number {
    const half = this.tuning.waitingAreaExitDoorWidth * 0.5
    return hashRange(seed * 31 + this.index * 7 + 1, -half, half)
  }

  /** Unity world position of a point in this room's local space. */
  toWorld(local: Vector3, out = new Vector3()): Vector3 {
    return out.copy(local).applyQuaternion(this.rotation).add(this.position)
  }

  /** The inverse. */
  toLocal(world: Vector3, out = new Vector3()): Vector3 {
    return out.copy(world).sub(this.position).applyQuaternion(_inverse.copy(this.rotation).invert())
  }

  /** World position of a crowd member, for the view. */
  worldPositionOf(member: CrowdEntry, out = new Vector3()): Vector3 {
    return this.toWorld(member.visual, out)
  }

  /**
   * 🔍 Which rotation a crowd member is drawn with — the room's, or their own.
   *
   * `LateUpdate` (`__0x2133d18.c`) writes a rotation exactly **once**, at :507
   * (`set_localRotation`), and that write sits inside the branch opened at :382
   * by `if ((entry.IsJumping & 1) == 0)`. So the room poses only people who have
   * finished walking in. Anyone still mid walk-in is skipped, and their
   * transform still holds what `Receive`'s `WalkAnimation` put there — a
   * **world** rotation (`set_rotation_Injected`), so it is not composed with the
   * room's.
   *
   * Lives here, not in `SessionView`, so a headless test can check which arm is
   * taken. B6 and B7 both shipped view-only errors that no test could reach.
   */
  memberRotation(member: CrowdEntry, out = new Quaternion()): Quaternion {
    if (member.isJumping) return member.anim.walkRotation(out)
    member.anim.crowdRotation(member.tiltX, member.tiltZ, out)
    return out.premultiply(this.rotation)
  }

  // -- the crowd simulation -------------------------------------------------

  /**
   * 🔍 The room runs in **two Unity phases**, and the split is readable.
   *
   * `dump.cs:712477` lists exactly two engine callbacks on `WaitingArea`:
   * `Awake` (0x2133BF0) and `LateUpdate` (0x2133D18). There is **no `Update`**.
   * `QueueController` on the other hand has `Update` (0x2121840), whose whole
   * body is `ReleaseFromTrays` → `foreach area: UpdateBoarding` →
   * `CheckAllCleared` → `CheckFailCondition`. Unity finishes every `Update`
   * before it starts any `LateUpdate`, so on the original the crowd solver runs
   * **after** the frame's releasing, boarding and win/lose checks — all of
   * which therefore read the *previous* frame's packing, never this one's.
   *
   * That is observable, not bookkeeping: `BoardOnePerson` times the walk-out as
   * `distance / boardingWalkSpeed` measured from where the person is standing,
   * so the phase decides how long each boarding takes.
   *
   * `update()` survives only as a wrapper for callers that drive one room on
   * its own (`waitingAreaGeometry.test.ts`). `GameSession.update` drives the
   * two halves separately, in the engine's order.
   *
   * ❓ PLACEHOLDER — where the DOTween half sits relative to
   * `QueueController.Update` is **not** recoverable: the doors, the walk-in and
   * the character animations are all DOTween, which ticks from its own
   * `MonoBehaviour` under a script execution order the IL2CPP dump does not
   * carry. They stay in the `Update` phase, where this port has always run
   * them; only the crowd solver moves. To settle it we would need the original
   * project's `ProjectSettings/ScriptExecutionOrder.asset`, which AssetRipper
   * did not export.
   */
  update(dt: number): void {
    this.updateTweens(dt)
    this.lateUpdateStep(dt)
  }

  /** The `Update`-phase half: the three DOTween-driven pieces. See `update`. */
  updateTweens(dt: number): void {
    this.updateDoors(dt)
    this.walkIn(dt)
    for (const entry of this.crowd) entry.anim.update(dt)
  }

  /**
   * The `DOLocalMove` half of `Receive`, plus its `OnComplete`:
   *
   *     entry.IsJumping = false;
   *     entry.SimPosition = character.transform.localPosition;
   *     character.ForceIdlePose();
   *     entry.BaseRotY = baseFacingAngle
   *                    + Random.Range(-randomRotationOffset, randomRotationOffset);
   *     character.transform.localRotation = Quaternion.Euler(0f, entry.BaseRotY, 0f);
   */
  private walkIn(dt: number): void {
    const duration = Math.max(this.tuning.waitingAreaWalkInDuration, 1e-6)
    const spread = this.tuning.waitingAreaRandomRotationOffset
    for (const entry of this.crowd) {
      if (!entry.isJumping) continue
      entry.walkElapsed += dt
      const k = Math.min(1, entry.walkElapsed / duration)
      // ❓ **PLACEHOLDER — this curve is a guess, and it is the one thing about
      // the walk-in that is.** `Receive` ends
      // `DOLocalMove(entry.SimPosition, jumpDuration).SetEase(...)` and IL2CPP
      // erased the ease argument, exactly as it did for the door tweens above
      // (see the ❓ note on `OpenExitDoors`). Everything else here is read:
      // `jumpDuration` 0.3 from `.ctor` (0x98), the target from `Receive`'s own
      // arithmetic, and the two-stage shape — walk to the entry point, then let
      // the crowd solver carry on — from the fact that `SimPosition` is set to
      // the entry point while the transform tweens to it.
      //
      // ⛔ **The clip cannot settle it, and this has now been measured rather
      // than guessed at.** The plan used to read "a frame series through one
      // passenger's walk-in — 9 frames at 30 fps, not obviously measurable, but
      // it has not been attempted". It has been:
      //
      //   - the platform occupies screen `180x74 + 72 + 196` at 332x720; every
      //     one of the clip's 899 frames was decoded to raw RGB and differenced
      //     against its predecessor;
      //   - a lone passenger is roughly 12 px across, so one walking shows as
      //     ~200-260 changed pixels inside a bbox under 40x40. Across the whole
      //     clip there is **exactly one** such frame, and no run of them.
      //
      // The reason is structural, not luck, which is why re-running it with a
      // better tracker will not help:
      //
      //   - at load the platform fills all at once — t = 0.90..1.73 s carries
      //     785 changed pixels falling to 0, with the bbox spanning nearly the
      //     whole platform every frame: many passengers settling together;
      //   - afterwards a walk-in only happens when a spot frees, and a spot only
      //     frees when someone boards. Boarding moves the entire crowd, so from
      //     t = 5 s on every frame changes 2000-6000 pixels.
      //
      // A walk-in is therefore never alone in a frame. Settling this needs a
      // capture the clip is not: a level paused mid-fill, or a higher
      // resolution. Recorded in `GAMEPLAY-CHECKLIST.md` §10.
      const eased = Ease.outQuad(k)
      entry.visual.lerpVectors(entry.walkFrom, entry.walkTo, eased)
      if (k < 1) continue

      entry.isJumping = false
      entry.sim.copy(entry.visual)
      entry.velocity.set(0, 0, 0)
      entry.anim.forceIdlePose()
      entry.anim.baseRotY =
        this.tuning.waitingAreaBaseFacingAngle + hashRange(entry.seed * 17 + 7, -spread, spread)
    }
  }

  /**
   * Port of `WaitingArea.LateUpdate`, in order:
   *
   *   1. rebuild `_activeColors` from who is actually in the room, sorted;
   *   2. re-read `SimPosition` from anyone still walking in;
   *   3. flow every settled person toward their colour's band, at `flowSpeed`;
   *   4. `overlapIterations` passes of pairwise separation, then clamp to bounds;
   *   5. `SmoothDamp` the drawn position toward the sim one, and run the
   *      roly-poly tilt spring off the resulting velocity.
   *
   * Two details in step 3 are easy to miss and both matter on screen: the X
   * error is **doubled** relative to Z (`(targetX - sim.x) * 2f`), so people
   * sort into colour columns faster than they drift forward; and the step is
   * `Mathf.Min(distance, dt * flowSpeed)`, so nobody overshoots their target.
   *
   * Driven from `GameSession.update` **after** `CheckFailCondition`, because
   * this is a `LateUpdate` and `QueueController.Update` is an `Update`. See the
   * comment block on `update` above.
   */
  lateUpdateStep(dt: number): void {
    const count = this.crowd.length
    if (count === 0) return

    const step = Math.min(dt, MAX_STEP)
    if (step <= 0) return

    const t = this.tuning
    const radius = t.waitingAreaCharacterRadius
    const halfX = t.waitingAreaBounds.x * 0.5
    const halfZ = t.waitingAreaBounds.y * 0.5

    // 1. _activeColors.Clear(); add each character's colour; Sort()
    this.activeColors.length = 0
    for (const entry of this.crowd) {
      if (!this.activeColors.includes(entry.color)) this.activeColors.push(entry.color)
    }
    this.activeColors.sort((a, b) => a - b)

    // 2. Anyone mid walk-in is driven by the tween, not the solver.
    for (const entry of this.crowd) {
      if (entry.isJumping) entry.sim.copy(entry.visual)
    }

    // 3. Flow toward this person's own slot.
    //
    // ⚠️ The target is the deviation documented on `crowdSlotTarget`; the walk
    // toward it below is the original's, line for line. The order is
    // `ColorType` first — so colours come out as contiguous columns, left to
    // right, the same order the original's bands had — then arrival order, so
    // the assignment is stable frame to frame and only shifts when the room
    // gains or loses someone. Walkers are ranked too, even though the flow
    // skips them: their slot has to be reserved or every landing would
    // reshuffle the room.
    this.slotOrder.length = 0
    for (const entry of this.crowd) this.slotOrder.push(entry)
    this.slotOrder.sort((a, b) => a.color - b.color || a.seed - b.seed)
    const lattice = crowdLattice(count, t)

    for (let slot = 0; slot < count; slot++) {
      const entry = this.slotOrder[slot]
      if (entry.isJumping) continue

      crowdSlotTarget(slot, count, t, lattice, _slot)

      const dx = (_slot.x - entry.sim.x) * 2
      const dz = _slot.z - entry.sim.z
      const sq = dx * dx + dz * dz
      if (sq > 0.001) {
        const d = Math.sqrt(sq)
        const move = Math.min(d, step * t.waitingAreaFlowSpeed)
        entry.sim.x += (dx / d) * move
        entry.sim.z += (dz / d) * move
      }
    }

    // 4. Separation, then bounds.
    const minDistance = radius + radius + t.waitingAreaSkinWidth
    const minDistanceSq = minDistance * minDistance
    // 🔍 The field, raw. `[Range(1, 10)]` (`dump.cs:712568`) is an **Inspector**
    // attribute: it bounds the slider an author drags, and Unity never applies
    // it at runtime. `LateUpdate__0x2133d18.c`:215-221,367 reads
    // `*(int *)(param_4 + 0xa8)` straight into `if (0 < iterations) { ... do
    // { ... } while (i < iterations); }` — so 0 really is zero passes, and a
    // clamp to 1 here invents a separation pass the original would not run.
    // The HUD slider (`main.ts`, min 1 max 10) is the right place for the range.
    const iterations = t.waitingAreaOverlapIterations

    for (let pass = 0; pass < iterations; pass++) {
      for (let i = 0; i < count; i++) {
        const a = this.crowd[i]
        for (let j = i + 1; j < count; j++) {
          const b = this.crowd[j]
          // Two people both still in the air never push each other.
          if (a.isJumping && b.isJumping) continue

          let dx = a.sim.x - b.sim.x
          let dz = a.sim.z - b.sim.z
          let sq = dx * dx + dz * dz
          if (sq >= minDistanceSq) continue

          if (sq < 0.0001) {
            // `Random.Range(0f, Mathf.PI * 2f)` — exactly coincident, so pick a
            // direction rather than dividing by zero.
            const angle = hash01(pass * 131 + i * 31 + j * 7 + this.index) * Math.PI * 2
            dx = Math.cos(angle)
            dz = Math.sin(angle)
            sq = 1
          }

          const d = Math.sqrt(sq)
          let push = minDistance - d
          let nx = dx / d
          let nz = dz / d

          // Same colour pushes straight apart; different colours are steered so
          // the lower `ColorType` ends up on the -X side. This is what keeps
          // the bands from interleaving once the room is packed.
          if (a.color !== b.color) {
            const ia = this.activeColors.indexOf(a.color)
            const ib = this.activeColors.indexOf(b.color)
            if (ia !== -1 && ib !== -1 && ia !== ib) {
              const want = ia < ib ? -1 : 1
              const have = dx >= 0 ? 1 : -1
              if (have !== want) {
                nz = nz * 0.2
                const length = Math.sqrt(nz * nz + 1)
                nx = want / length
                nz = nz / length
                push = Math.max(push, radius * 0.15)
              }
            }
          }

          if (a.isJumping) {
            b.sim.x -= nx * push
            b.sim.z -= nz * push
          } else if (b.isJumping) {
            a.sim.x += nx * push
            a.sim.z += nz * push
          } else {
            const hx = nx * push * 0.5
            const hz = nz * push * 0.5
            a.sim.x += hx
            a.sim.z += hz
            b.sim.x -= hx
            b.sim.z -= hz
          }
        }
      }

      for (const entry of this.crowd) {
        if (entry.isJumping) continue
        entry.sim.x = clamp(entry.sim.x, radius - halfX, halfX - radius)
        entry.sim.z = clamp(entry.sim.z, radius - halfZ, halfZ - radius)
      }
    }

    // 5. Visual chase + roly-poly.
    const settleLerp = Math.min(1, step * 10)
    const settleVelocityLerp = Math.min(1, step * 15)

    for (const entry of this.crowd) {
      if (entry.isJumping) continue

      entry.sim.y = CHARACTER_Y
      smoothDamp(entry.visual, entry.sim, entry.velocity, t.waitingAreaSmoothTime, step, _damped)
      if (
        _damped.distanceToSquared(entry.sim) < 1e-5 ||
        entry.velocity.lengthSq() < 1e-4
      ) {
        entry.velocity.set(0, 0, 0)
        _damped.copy(entry.sim)
      }
      entry.visual.copy(_damped)

      const speedSq = entry.velocity.lengthSq()
      const moving = speedSq > MOVING_SPEED_SQ

      if (!moving) {
        // Coming to a halt injects a kick, which is the "cartoonish whiplash".
        if (entry.wasMoving) {
          entry.tiltVelocityX += entry.tiltX * t.waitingAreaStopWobbleBoost
          entry.tiltVelocityZ += entry.tiltZ * t.waitingAreaStopWobbleBoost
        }
        entry.stoppedTimer += step
        entry.wasMoving = false
        if (entry.stoppedTimer >= t.waitingAreaMaxWobbleDuration) {
          entry.tiltX += (0 - entry.tiltX) * settleLerp
          entry.tiltZ += (0 - entry.tiltZ) * settleLerp
          entry.tiltVelocityX += (0 - entry.tiltVelocityX) * settleVelocityLerp
          entry.tiltVelocityZ += (0 - entry.tiltVelocityZ) * settleVelocityLerp
          if (Math.abs(entry.tiltX) < 0.01 && Math.abs(entry.tiltZ) < 0.01) {
            entry.tiltX = 0
            entry.tiltZ = 0
            entry.tiltVelocityX = 0
            entry.tiltVelocityZ = 0
          }
          continue
        }
      } else {
        entry.stoppedTimer = 0
        entry.wasMoving = true
      }

      // The spring. Tilt leans *into* travel: forward motion (-Z) pitches the
      // body forward about X, sideways motion rolls it about Z.
      const maxTilt = t.waitingAreaMaxTilt
      const targetX = clamp(-entry.velocity.z * t.waitingAreaTiltMultiplier, -maxTilt, maxTilt)
      const targetZ = clamp(entry.velocity.x * t.waitingAreaTiltMultiplier, -maxTilt, maxTilt)

      entry.tiltVelocityX +=
        ((targetX - entry.tiltX) * t.waitingAreaWobbleStiffness -
          entry.tiltVelocityX * t.waitingAreaWobbleDamping) *
        step
      entry.tiltVelocityZ +=
        ((targetZ - entry.tiltZ) * t.waitingAreaWobbleStiffness -
          entry.tiltVelocityZ * t.waitingAreaWobbleDamping) *
        step
      entry.tiltVelocityX = clamp(entry.tiltVelocityX, -MAX_TILT_VELOCITY, MAX_TILT_VELOCITY)
      entry.tiltVelocityZ = clamp(entry.tiltVelocityZ, -MAX_TILT_VELOCITY, MAX_TILT_VELOCITY)

      entry.tiltX = clamp(entry.tiltX + entry.tiltVelocityX * step, -maxTilt * 1.25, maxTilt * 1.25)
      entry.tiltZ = clamp(entry.tiltZ + entry.tiltVelocityZ * step, -maxTilt * 1.25, maxTilt * 1.25)
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value
}

/**
 * `UnityEngine.Vector3.SmoothDamp` with no speed limit, verbatim — a critically
 * damped spring, not a lerp, and the difference shows: a lerp overshoots nothing
 * and so never produces the small trailing velocity the tilt spring feeds on.
 */
function smoothDamp(
  current: Vector3,
  target: Vector3,
  velocity: Vector3,
  smoothTime: number,
  dt: number,
  out: Vector3,
): Vector3 {
  const time = Math.max(0.0001, smoothTime)
  const omega = 2 / time
  const x = omega * dt
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)

  const cx = current.x - target.x
  const cy = current.y - target.y
  const cz = current.z - target.z

  const tempX = (velocity.x + omega * cx) * dt
  const tempY = (velocity.y + omega * cy) * dt
  const tempZ = (velocity.z + omega * cz) * dt

  velocity.set(
    (velocity.x - omega * tempX) * exp,
    (velocity.y - omega * tempY) * exp,
    (velocity.z - omega * tempZ) * exp,
  )

  out.set(
    target.x + (cx + tempX) * exp,
    target.y + (cy + tempY) * exp,
    target.z + (cz + tempZ) * exp,
  )

  // Prevent overshooting.
  const ox = target.x - current.x
  const oy = target.y - current.y
  const oz = target.z - current.z
  if (ox * (out.x - target.x) + oy * (out.y - target.y) + oz * (out.z - target.z) > 0) {
    out.copy(target)
    velocity.set(0, 0, 0)
  }
  return out
}

const _damped = new Vector3()
const _slot = { x: 0, z: 0 }
const _inverse = new Quaternion()
